import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Button,
  Divider,
  TextField,
  MenuItem,
  Alert,
  FormControl,
  Select,
  InputLabel,
  IconButton,
  Autocomplete,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import companyApi from "../../api/companyApi";
import {
  ALLOWED_STAGED_COMPANY_FIELDS,
  COUNTRY_OPTIONS,
  COMPANY_LIFECYCLE_OPTIONS,
  COMPANY_LIFECYCLE_STAGE_OPTIONS,
  COMPANY_TYPE_OPTIONS,
  CORPORATE_STRUCTURE_OPTIONS,
  VALID_STAGES_BY_LIFECYCLE,
} from "../../constants/companyFields";
import { getCommodityMeta, COMMODITY_META } from "./functions";
import {
  mapChoiceByOptions,
  normalizeChoiceForPayload,
  commodityListFromValue,
  normalizeCommodityInputList,
  normalizeCommoditiesForPayload,
  rowsFromBackend,
  normalizeRows,
  hasDuplicatePairs,
  hasIncompleteRows,
  areCommodityListsEqual,
  areTickerArraysEqual,
  normalizeFieldValue,
  ensureUrl,
} from "./summaryTabUtils";

const EXCHANGE_OPTIONS = [
  "TSX",
  "TSXV",
  "NYSE",
  "NASDAQ",
  "LSE",
  "ASX",
  "OTC",
  "TADAWUL",
  "CSE",
  "IDX",
  "BIST",
  "KRX",
  "HKEX",
  "SSE",
  "FSX",
];

export default function SummaryTabEditor({
  company,
  canEditCompany,
  onCompanyUpdated,
  renderDisplay,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [tickerRows, setTickerRows] = useState([{ exchange: "", ticker: "" }]);
  const [saveState, setSaveState] = useState({ loading: false, error: null, success: null });

  const profile = company?.profile || {};
  const normalizedCompanyType = mapChoiceByOptions(profile?.company_type, COMPANY_TYPE_OPTIONS);
  const normalizedCompanyLifecycle = mapChoiceByOptions(
    profile?.company_lifecycle,
    COMPANY_LIFECYCLE_OPTIONS
  );
  const normalizedCompanyLifecycleStage = mapChoiceByOptions(
    profile?.company_lifecycle_stage,
    COMPANY_LIFECYCLE_STAGE_OPTIONS
  );
  const normalizedCorporateStructure = mapChoiceByOptions(
    profile?.corporate_structure,
    CORPORATE_STRUCTURE_OPTIONS
  );

  const initDraftFromCompany = useCallback(
    (companyData) => {
      const profileData = companyData?.profile || {};
      const managementPerson = (companyData?.management_people || [])[0] || {};
      return {
        name: companyData?.name || "",
        corporate_description: profileData?.corporate_description || "",
        website_url: profileData?.website_url || "",
        hq_country: profileData?.hq_country || "",
        hq_city: profileData?.hq_city || "",
        contact_email: profileData?.contact_email || "",
        contact_phone: profileData?.contact_phone || "",
        year_founded: profileData?.year_founded ?? "",
        corporate_structure: mapChoiceByOptions(
          profileData?.corporate_structure,
          CORPORATE_STRUCTURE_OPTIONS
        ),
        company_type: mapChoiceByOptions(profileData?.company_type, COMPANY_TYPE_OPTIONS),
        company_lifecycle: mapChoiceByOptions(
          profileData?.company_lifecycle,
          COMPANY_LIFECYCLE_OPTIONS
        ),
        company_lifecycle_stage: mapChoiceByOptions(
          profileData?.company_lifecycle_stage,
          COMPANY_LIFECYCLE_STAGE_OPTIONS
        ),
        commodities: normalizeCommodityInputList(commodityListFromValue(profileData?.commodities)),
        recent_news_link: profileData?.recent_news_link || "",
        corporate_presentation: profileData?.corporate_presentation || "",
        featured_video_link: profileData?.featured_video_link || "",
        executive_name: managementPerson?.executive_name || "",
        role_title: managementPerson?.role_title || "",
        background_summary: managementPerson?.background_summary || "",
        tenure_start_year: managementPerson?.tenure_start_year ?? "",
        estimated_lower_bound_start_year: managementPerson?.estimated_lower_bound_start_year ?? "",
        estimated_upper_bound_start_year: managementPerson?.estimated_upper_bound_start_year ?? "",
      };
    },
    []
  );

  useEffect(() => {
    if (company) {
      setDraft(initDraftFromCompany(company));
      setTickerRows(rowsFromBackend(company?.profile?.tickers_exchanges));
    }
  }, [company, initDraftFromCompany]);

  const handleStartEdit = () => {
    if (!company) return;
    if (!canEditCompany) return;
    setDraft(initDraftFromCompany(company));
    setTickerRows(rowsFromBackend(company?.profile?.tickers_exchanges));
    setSaveState({ loading: false, error: null, success: null });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDraft(initDraftFromCompany(company));
    setTickerRows(rowsFromBackend(company?.profile?.tickers_exchanges));
    setSaveState({ loading: false, error: null, success: null });
  };

  const handleDraftChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanyTypeChange = (event) => {
    const value = event?.target?.value ?? "";
    setDraft((prev) => ({
      ...prev,
      company_type: value,
      company_lifecycle:
        value === "royalty_streaming"
          ? "na"
          : prev.company_lifecycle && prev.company_lifecycle !== "na"
          ? prev.company_lifecycle
          : "",
      company_lifecycle_stage: value === "royalty_streaming" ? "" : prev.company_lifecycle_stage,
    }));
  };

  const handleTickerChange = (index, field) => (event) => {
    const value = event?.target?.value ?? "";
    setTickerRows((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next[index] = { ...next[index], [field]: field === "ticker" ? value.toUpperCase() : value };
      return next;
    });
  };

  const addTickerRow = () => {
    setTickerRows((prev) => [...(Array.isArray(prev) ? prev : []), { exchange: "", ticker: "" }]);
  };

  const removeTickerRow = (index) => {
    setTickerRows((prev) => {
      const next = (prev || []).filter((_, i) => i !== index);
      return next.length ? next : [{ exchange: "", ticker: "" }];
    });
  };

  const availableLifecycleStages = useMemo(() => {
    const lifecycle = draft.company_lifecycle || profile?.company_lifecycle;
    if (!lifecycle || lifecycle === "na") return [];
    const allowed = VALID_STAGES_BY_LIFECYCLE[lifecycle] || [];
    if (!allowed.length) return [];
    return COMPANY_LIFECYCLE_STAGE_OPTIONS.filter((option) => allowed.includes(option.value));
  }, [draft.company_lifecycle, profile?.company_lifecycle]);

  const currentCompanyType = draft.company_type || normalizedCompanyType || "";
  const isRoyalty = currentCompanyType === "royalty_streaming";

  useEffect(() => {
    if (!isEditing) return;
    if (isRoyalty && draft.company_lifecycle !== "na") {
      setDraft((prev) => ({
        ...prev,
        company_lifecycle: "na",
        company_lifecycle_stage: "",
      }));
    }
  }, [isRoyalty, isEditing, draft.company_lifecycle]);

  const getValidationError = () => {
    const lifecycle = draft.company_lifecycle || profile?.company_lifecycle;
    const stage = draft.company_lifecycle_stage;
    if (draft.year_founded !== "" && draft.year_founded !== null && draft.year_founded !== undefined) {
      const yearString = `${draft.year_founded}`.trim();
      if (!/^\d{4}$/.test(yearString)) {
        return "Year founded must be a 4-digit year.";
      }
    }
    if (draft.website_url) {
      try {
        const candidate = draft.website_url.includes("://")
          ? draft.website_url
          : `https://${draft.website_url}`;
        new URL(candidate);
      } catch {
        return "Website URL must be valid.";
      }
    }
    if (draft.contact_email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(draft.contact_email.trim())) {
        return "Contact email must be valid.";
      }
    }
    if (draft.contact_phone) {
      const phoneRegex = /^[+()0-9.\-\s]{7,20}$/;
      if (!phoneRegex.test(draft.contact_phone.trim())) {
        return "Contact phone must be valid.";
      }
    }
    if (draft.recent_news_link) {
      try {
        const candidate = ensureUrl(draft.recent_news_link);
        new URL(candidate);
      } catch {
        return "Recent news link must be valid.";
      }
    }
    if (draft.corporate_presentation) {
      try {
        const candidate = ensureUrl(draft.corporate_presentation);
        new URL(candidate);
      } catch {
        return "Corporate presentation link must be valid.";
      }
    }
    if (draft.featured_video_link) {
      try {
        const candidate = ensureUrl(draft.featured_video_link);
        new URL(candidate);
      } catch {
        return "Featured video link must be valid.";
      }
    }
    if (hasDuplicatePairs(tickerRows)) return "Duplicate tickers detected.";
    if (hasIncompleteRows(tickerRows))
      return "Each ticker row needs both an exchange and ticker, or leave it blank.";
    if (stage) {
      if (!lifecycle || lifecycle === "na") {
        return "Select a lifecycle before choosing a stage.";
      }
      const allowedStages = VALID_STAGES_BY_LIFECYCLE[lifecycle] || [];
      if (allowedStages.length && !allowedStages.includes(stage)) {
        return "Selected lifecycle stage is not valid for this lifecycle.";
      }
    }
    return null;
  };

  const buildPayload = () => {
    const payload = {};
    const profilePayload = {};
    const managementPerson = (company?.management_people || [])[0] || {};
    const currentTickers = normalizeRows(rowsFromBackend(profile?.tickers_exchanges));
    const nextTickers = normalizeRows(tickerRows);

    const normalizedDraftChoices = {
      company_type: normalizeChoiceForPayload(draft.company_type, COMPANY_TYPE_OPTIONS),
      company_lifecycle: normalizeChoiceForPayload(draft.company_lifecycle, COMPANY_LIFECYCLE_OPTIONS),
      company_lifecycle_stage: normalizeChoiceForPayload(
        draft.company_lifecycle_stage,
        COMPANY_LIFECYCLE_STAGE_OPTIONS
      ),
      corporate_structure: normalizeChoiceForPayload(draft.corporate_structure, CORPORATE_STRUCTURE_OPTIONS),
    };
    if (normalizedDraftChoices.company_lifecycle_stage === null) {
      normalizedDraftChoices.company_lifecycle_stage = "";
    }
    const normalizedCurrentChoices = {
      company_type: normalizedCompanyType || null,
      company_lifecycle: normalizedCompanyLifecycle || null,
      company_lifecycle_stage: normalizedCompanyLifecycleStage || null,
      corporate_structure: normalizedCorporateStructure || null,
    };

    ALLOWED_STAGED_COMPANY_FIELDS.forEach((field) => {
      let nextVal;
      if (field === "tickers_exchanges") {
        nextVal = nextTickers;
      } else if (field === "commodities") {
        nextVal = normalizeCommoditiesForPayload(draft.commodities);
      } else if (field in normalizedDraftChoices) {
        nextVal = normalizedDraftChoices[field];
      } else {
        nextVal = normalizeFieldValue(field, draft[field]);
      }

      const currentVal =
        field === "tickers_exchanges"
          ? currentTickers
          : field === "commodities"
          ? normalizeCommoditiesForPayload(profile?.[field])
          : normalizedCurrentChoices[field] ?? normalizeFieldValue(field, profile?.[field]);

      const isDifferent =
        field === "tickers_exchanges"
          ? JSON.stringify(nextVal === undefined ? null : nextVal) !==
            JSON.stringify(currentVal === undefined ? null : currentVal)
          : field === "commodities"
          ? !areCommodityListsEqual(nextVal, currentVal)
          : JSON.stringify(nextVal === undefined ? null : nextVal) !==
            JSON.stringify(currentVal === undefined ? null : currentVal);
      if (isDifferent) {
        if (field === "name") {
          payload[field] = nextVal;
        } else {
          profilePayload[field] = nextVal;
        }
      }
    });
    if (Object.keys(profilePayload).length) {
      payload.draft_profile = profilePayload;
    }
    const managementPayload = {
      executive_name: draft.executive_name || "",
      role_title: draft.role_title || "",
      background_summary: draft.background_summary || "",
      tenure_start_year: draft.tenure_start_year === "" ? null : Number(draft.tenure_start_year),
      estimated_lower_bound_start_year:
        draft.estimated_lower_bound_start_year === ""
          ? null
          : Number(draft.estimated_lower_bound_start_year),
      estimated_upper_bound_start_year:
        draft.estimated_upper_bound_start_year === ""
          ? null
          : Number(draft.estimated_upper_bound_start_year),
    };
    if (managementPayload.tenure_start_year !== (managementPerson.tenure_start_year ?? null)) {
      managementPayload.estimated_lower_bound_start_year = managementPayload.tenure_start_year;
      managementPayload.estimated_upper_bound_start_year = managementPayload.tenure_start_year;
    }
    const managementChanged =
      managementPayload.executive_name !== (managementPerson.executive_name || "") ||
      managementPayload.role_title !== (managementPerson.role_title || "") ||
      managementPayload.background_summary !== (managementPerson.background_summary || "") ||
      managementPayload.tenure_start_year !==
        (managementPerson.tenure_start_year ?? null) ||
      managementPayload.estimated_lower_bound_start_year !==
        (managementPerson.estimated_lower_bound_start_year ?? null) ||
      managementPayload.estimated_upper_bound_start_year !==
        (managementPerson.estimated_upper_bound_start_year ?? null);
    if (managementChanged) {
      payload.draft_management_people = [managementPayload];
    }
    return payload;
  };

  const handleSave = async () => {
    if (!company?.id) return;
    const validationError = getValidationError();
    if (validationError) {
      setSaveState({ loading: false, error: validationError, success: null });
      return;
    }
    const payload = buildPayload();
    const nextTickers = normalizeRows(tickerRows);
    if (!areTickerArraysEqual(nextTickers, profile?.tickers_exchanges)) {
      payload.draft_profile = payload.draft_profile || {};
      payload.draft_profile.tickers_exchanges = nextTickers;
    }
    if (!Object.keys(payload).length) {
      setSaveState({ loading: false, error: "No changes to save.", success: null });
      return;
    }
    setSaveState({ loading: true, error: null, success: null });
    try {
      await companyApi.updateCompany(company.slug, payload);
      setSaveState({
        loading: false,
        error: null,
        success: "Company draft saved.",
      });
      setIsEditing(false);
      setDraft(initDraftFromCompany(company));
      setTickerRows(rowsFromBackend(company?.profile?.tickers_exchanges));
      onCompanyUpdated && onCompanyUpdated();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.proposed_changes ||
        "Unable to save company draft.";
      setSaveState({ loading: false, error: detail, success: null });
    }
  };

  const duplicatePairs = hasDuplicatePairs(tickerRows);
  const incompleteRows = hasIncompleteRows(tickerRows);
  const disableSave = duplicatePairs || incompleteRows || saveState.loading;

  return (
    <>
      {!isEditing && (
        <>
          {saveState.success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {saveState.success}
            </Alert>
          )}
          {renderDisplay
            ? renderDisplay({
                headerAction:
                  canEditCompany && (
                    <Button variant="outlined" onClick={handleStartEdit}>
                      Edit
                    </Button>
                  ),
              })
            : null}
        </>
      )}

      {isEditing && (
        <>
          {saveState.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveState.error}
            </Alert>
          )}
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: { xs: 1, sm: 3 }, height: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    Company Description
                  </Typography>
                </Box>

                <TextField
                  multiline
                  minRows={4}
                  label="Corporate Description"
                  fullWidth
                  value={draft.corporate_description}
                  onChange={handleDraftChange("corporate_description")}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: { xs: 1, sm: 3 }, height: "100%" }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Featured Media
                </Typography>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Recent News Link"
                        value={draft.recent_news_link ?? profile?.recent_news_link ?? ""}
                        onChange={handleDraftChange("recent_news_link")}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Corporate Presentation Link"
                        value={draft.corporate_presentation ?? profile?.corporate_presentation ?? ""}
                        onChange={handleDraftChange("corporate_presentation")}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Featured Video Link"
                        value={draft.featured_video_link ?? profile?.featured_video_link ?? ""}
                        onChange={handleDraftChange("featured_video_link")}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: { xs: 1, sm: 3 }, height: "100%" }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Snapshot
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={2}>
                      <TextField
                        label="Company Name"
                        value={draft.name ?? company?.name ?? ""}
                        onChange={handleDraftChange("name")}
                        fullWidth
                      />
                      <TextField
                        label="Website URL"
                        value={draft.website_url ?? profile?.website_url ?? ""}
                        onChange={handleDraftChange("website_url")}
                        fullWidth
                      />
                      <TextField
                        select
                        label="Company Type"
                        value={draft.company_type ?? normalizedCompanyType ?? ""}
                        onChange={handleCompanyTypeChange}
                        fullWidth
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {COMPANY_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Lifecycle"
                        value={isRoyalty ? "na" : draft.company_lifecycle ?? normalizedCompanyLifecycle ?? ""}
                        onChange={(e) => {
                          const lifecycleVal = e.target.value;
                          setDraft((prev) => ({
                            ...prev,
                            company_lifecycle: lifecycleVal,
                            company_lifecycle_stage: (() => {
                              const allowedStages = VALID_STAGES_BY_LIFECYCLE[lifecycleVal] || [];
                              const currentStage = prev.company_lifecycle_stage;
                              if (!lifecycleVal || lifecycleVal === "na") return "";
                              return allowedStages.includes(currentStage) ? currentStage : "";
                            })(),
                          }));
                        }}
                        fullWidth
                        disabled={isRoyalty}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {COMPANY_LIFECYCLE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Lifecycle Stage"
                        value={draft.company_lifecycle_stage ?? normalizedCompanyLifecycleStage ?? ""}
                        onChange={handleDraftChange("company_lifecycle_stage")}
                        fullWidth
                        disabled={
                          isRoyalty ||
                          !availableLifecycleStages.length ||
                          !draft.company_lifecycle ||
                          draft.company_lifecycle === "na"
                        }
                        helperText={
                          isRoyalty
                            ? "Lifecycle is N/A for royalty/streaming companies."
                            : draft.company_lifecycle && draft.company_lifecycle !== "na"
                            ? "Stages limited by lifecycle selection."
                            : "Select a lifecycle to enable stage choices."
                        }
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {availableLifecycleStages.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Corporate Structure"
                        value={draft.corporate_structure ?? normalizedCorporateStructure ?? ""}
                        onChange={handleDraftChange("corporate_structure")}
                        fullWidth
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {CORPORATE_STRUCTURE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box>
                        <Autocomplete
                          multiple
                          freeSolo
                          options={Object.keys(COMMODITY_META)}
                          value={draft.commodities || []}
                          onChange={(_, value) =>
                            setDraft((prev) => ({
                              ...prev,
                              commodities: normalizeCommodityInputList(value),
                            }))
                          }
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const meta = getCommodityMeta(option);
                              return (
                                <Chip
                                  {...getTagProps({ index })}
                                  label={meta?.symbol || option}
                                  size="small"
                                  sx={{
                                    backgroundColor: meta?.color || "action.hover",
                                    color: "#0f172a",
                                    fontWeight: 700,
                                  }}
                                  title={meta?.label || option}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Commodities"
                              placeholder="Add commodity"
                              helperText="Search or type to add; press Enter to create a new one."
                            />
                          )}
                        />
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={2}>
                      <TextField
                        label="Contact Email"
                        value={draft.contact_email ?? profile?.contact_email ?? ""}
                        onChange={handleDraftChange("contact_email")}
                        fullWidth
                      />
                      <TextField
                        label="Contact Phone"
                        value={draft.contact_phone ?? profile?.contact_phone ?? ""}
                        onChange={handleDraftChange("contact_phone")}
                        fullWidth
                      />
                      <TextField
                        label="Year Founded"
                        value={draft.year_founded ?? profile?.year_founded ?? ""}
                        onChange={handleDraftChange("year_founded")}
                        fullWidth
                        slotProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 4 }}
                      />
                      <TextField
                        label="HQ City"
                        value={draft.hq_city ?? profile?.hq_city ?? ""}
                        onChange={handleDraftChange("hq_city")}
                        fullWidth
                      />
                      <TextField
                        select
                        label="HQ Country"
                        value={draft.hq_country ?? profile?.hq_country ?? ""}
                        onChange={handleDraftChange("hq_country")}
                        fullWidth
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {COUNTRY_OPTIONS.map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Typography variant="subtitle2">Tickers</Typography>
                        {duplicatePairs && (
                          <Alert severity="error" sx={{ py: 0.5 }}>
                            Duplicate tickers detected. Remove duplicates before saving.
                          </Alert>
                        )}
                        {incompleteRows && !duplicatePairs && (
                          <Alert severity="warning" sx={{ py: 0.5 }}>
                            Each ticker row needs both an exchange and ticker, or leave it blank.
                          </Alert>
                        )}
                        <Stack spacing={1.25}>
                          {tickerRows.map((row, idx) => {
                            const exchangeError = hasDuplicatePairs(tickerRows);
                            const tickerHasSpace = (row.ticker || "").includes(" ");
                            return (
                              <Box
                                key={idx}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "160px 1fr 40px",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <FormControl size="small" fullWidth error={exchangeError}>
                                  <InputLabel>Exchange</InputLabel>
                                  <Select
                                    label="Exchange"
                                    value={row.exchange || ""}
                                    onChange={handleTickerChange(idx, "exchange")}
                                  >
                                    {EXCHANGE_OPTIONS.map((ex) => (
                                      <MenuItem key={ex} value={ex}>
                                        {ex}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  label="Ticker"
                                  value={row.ticker || ""}
                                  onChange={handleTickerChange(idx, "ticker")}
                                  fullWidth
                                  error={tickerHasSpace || exchangeError}
                                  helperText={
                                    tickerHasSpace
                                      ? "No spaces."
                                      : exchangeError
                                      ? "Duplicate tickers detected."
                                      : ""
                                  }
                                />
                                <IconButton
                                  aria-label="remove ticker"
                                  onClick={() => removeTickerRow(idx)}
                                  size="small"
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Box>
                            );
                          })}
                        </Stack>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            mt: 0.5,
                          }}
                        >
                          <Button startIcon={<AddIcon />} onClick={addTickerRow} size="small">
                            Add ticker
                          </Button>
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Key Executive Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack spacing={2}>
                          <TextField
                            label="Name"
                            value={draft.executive_name ?? ""}
                            onChange={handleDraftChange("executive_name")}
                            fullWidth
                          />
                          <TextField
                            label="Title"
                            value={draft.role_title ?? ""}
                            onChange={handleDraftChange("role_title")}
                            fullWidth
                          />
                          <TextField
                            label="Tenure Start Year"
                            value={draft.tenure_start_year ?? ""}
                            onChange={handleDraftChange("tenure_start_year")}
                            fullWidth
                            slotProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 4 }}
                          />
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Background Summary"
                          value={draft.background_summary ?? ""}
                          onChange={handleDraftChange("background_summary")}
                          fullWidth
                          multiline
                          minRows={3}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {canEditCompany && (
                  <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleSave} disabled={disableSave}>
                      {saveState.loading ? "Saving..." : "Save Edits as Draft"}
                    </Button>
                    <Button variant="text" onClick={handleCancelEdit} disabled={saveState.loading}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </>
      )}

    </>
  );
}
